// Session registry — pure derivations over the turn engine's busy map and
// the thread / project lists: which projects have a turn running, which
// threads are unread, which projects carry an unread thread. Feeds the
// thread switcher (screen 07) and the top-bar activity dot (screen 08).
//
// No state of its own — every field is a `useMemo` over its inputs, so
// this hook is trivially correct and cheap to recompute.

import { useMemo } from 'react'
import type { Project, Thread } from './types'

export interface SessionRegistryInput {
  busyByThread: Record<string, boolean>
  threads: Thread[]
  projects: Project[]
  activeThreadId: string | null
  activeProjectId: string | null
}

export interface SessionRegistry {
  /** True while any thread has a turn running. */
  anyBusy: boolean
  /** Project ids with at least one busy thread (among loaded threads). */
  busyProjects: ReadonlySet<string>
  /** threadId → 1 for unread threads (dots are rendered, not counts).
   *  Excludes the active thread — viewing it clears the dot. */
  unreadByThread: Record<string, number>
  /** Project ids carrying an unread thread. */
  unreadProjects: ReadonlySet<string>
}

/**
 * Unread iff there is activity past the read cursor:
 *  - no cursor + activity only at creation → not unread (a thread you just
 *    made yourself, before any turn, must not badge you)
 *  - no cursor + later activity → unread (written into before you opened it)
 *  - cursor present → unread iff last_message_at > last_read_at
 */
function isThreadUnread(t: Thread): boolean {
  const lastMessage = t.last_message_at ?? t.updated_at
  const lastRead = t.last_read_at ?? null
  return lastRead === null
    ? lastMessage > t.created_at
    : lastMessage > lastRead
}

export function useSessionRegistry(
  input: SessionRegistryInput,
): SessionRegistry {
  const { busyByThread, threads, projects, activeThreadId, activeProjectId } =
    input

  const anyBusy = useMemo(
    () => Object.values(busyByThread).some(Boolean),
    [busyByThread],
  )

  const busyProjects = useMemo(() => {
    const projectByThread = new Map(threads.map((t) => [t.id, t.project_id]))
    const set = new Set<string>()
    for (const tid of Object.keys(busyByThread)) {
      if (!busyByThread[tid]) continue
      const pid = projectByThread.get(tid)
      if (pid) set.add(pid)
    }
    return set
  }, [busyByThread, threads])

  const unreadByThread = useMemo(() => {
    const map: Record<string, number> = {}
    for (const t of threads) {
      if (t.id === activeThreadId) continue
      if (isThreadUnread(t)) map[t.id] = 1
    }
    return map
  }, [threads, activeThreadId])

  const unreadProjects = useMemo(() => {
    const set = new Set<string>()
    // Off-screen projects: trust the server-side count snapshotted at
    // list time.
    for (const p of projects) {
      if (p.id === activeProjectId) continue
      if ((p.unread_thread_count ?? 0) > 0) set.add(p.id)
    }
    // The active project's freshest signal is the live thread list.
    if (activeProjectId) {
      const hasUnread = threads.some(
        (t) => t.id !== activeThreadId && isThreadUnread(t),
      )
      if (hasUnread) set.add(activeProjectId)
    }
    return set
  }, [projects, activeProjectId, threads, activeThreadId])

  return { anyBusy, busyProjects, unreadByThread, unreadProjects }
}
